"use client";

import React, { useEffect, useMemo, useState } from "react";
import axiosInstance from "../../utils/axiosInstance";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
    UploadCloud,
    FileText,
    Search,
    RotateCcw,
    Filter,
    CheckCircle2,
    XCircle,
    Table2,
    Info,
    AlertTriangle,
    ArrowUpDown,
} from "lucide-react";
import { useDropzone } from "react-dropzone";

const errMsg = (e) => e?.response?.data?.error || e?.message || "Request failed";

function friendlyNetworkError(e) {
    const status = e?.response?.status;
    if (!status && e?.message === "Network Error") {
        return "Network Error: the API may be unreachable, blocked by CORS, or returning an error before the browser can read it.";
    }
    return errMsg(e);
}

function cx(...xs) {
    return xs.filter(Boolean).join(" ");
}

function sanitize(obj) {
    const out = {};
    for (const [k, v] of Object.entries(obj)) {
        if (v == null || v === "") continue;
        if (typeof v === "number" && !Number.isFinite(v)) continue;
        out[k] = v;
    }
    return out;
}

function Badge({ tone = "slate", children }) {
    const tones = {
        slate: "bg-slate-100 text-slate-700 border-slate-200",
        emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
        rose: "bg-rose-50 text-rose-700 border-rose-200",
        amber: "bg-amber-50 text-amber-800 border-amber-200",
    };
    return (
        <span
            className={cx(
                "inline-flex items-center rounded-full border px-3 py-1 text-xs font-semibold",
                tones[tone]
            )}
        >
            {children}
        </span>
    );
}

function PillTabs({ tabs, value, onChange }) {
    return (
        <div className="inline-flex rounded-2xl border border-slate-200 bg-white p-1 shadow-sm overflow-x-auto no-scrollbar">
            {tabs.map((t) => {
                const active = t.value === value;
                return (
                    <button
                        key={t.value}
                        type="button"
                        onClick={() => onChange(t.value)}
                        className={cx(
                            "h-11 rounded-xl px-4 text-sm font-semibold outline-none focus:ring-2 focus:ring-slate-400/70 whitespace-nowrap",
                            active ? "bg-slate-900 text-white" : "text-slate-700 hover:bg-slate-100"
                        )}
                    >
                        {t.label}
                    </button>
                );
            })}
        </div>
    );
}

function ScrollTable({ columns, rows, maxHeight = "max-h-[560px]" }) {
    return (
        <div className={cx("overflow-auto rounded-2xl border border-slate-200 bg-white shadow-sm", maxHeight)}>
            <table className="min-w-[1100px] w-full table-auto text-left text-xs sm:text-sm">
                <thead className="bg-slate-50 sticky top-0 z-10">
                    <tr className="border-b border-slate-200">
                        {columns.map((col) => (
                            <th key={String(col.key)} className="px-3 py-3 font-semibold text-slate-700 whitespace-nowrap">
                                {col.header}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {rows.map((row, i) => (
                        <tr key={i} className="odd:bg-white even:bg-slate-50/60 border-b border-slate-100">
                            {columns.map((col) => (
                                <td key={String(col.key)} className="px-3 py-2 whitespace-nowrap text-slate-700">
                                    {String(row[col.key] ?? "")}
                                </td>
                            ))}
                        </tr>
                    ))}
                    {rows.length === 0 && (
                        <tr>
                            <td colSpan={columns.length} className="px-3 py-10 text-center text-sm text-slate-500">
                                No results
                            </td>
                        </tr>
                    )}
                </tbody>
            </table>
        </div>
    );
}

function GridNumber({ id, label, value, onChange, step = 0.001, disabled }) {
    return (
        <div className="flex flex-col gap-1">
            <label htmlFor={id} className="text-sm font-semibold text-slate-700">
                {label}
            </label>
            <input
                id={id}
                type="number"
                step={step}
                disabled={disabled}
                className={cx(
                    "h-11 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-900 shadow-sm outline-none focus:ring-2 focus:ring-slate-400/70",
                    disabled && "opacity-60 bg-slate-50"
                )}
                value={value ?? ""}
                onChange={(e) => onChange(e.target.value)}
            />
        </div>
    );
}

function USVInputs({ constraints, setConstraints, lockUmax = false }) {
    const set = (k) => (v) => setConstraints({ ...constraints, [k]: v === "" ? "" : Number(v) });

    return (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <GridNumber id="uMin" label="U value min" value={constraints.uMin ?? ""} onChange={set("uMin")} />
            <GridNumber id="vMin" label="Tvis min" value={constraints.vMin ?? ""} onChange={set("vMin")} />
            <GridNumber id="sMin" label="SHGC min" value={constraints.sMin ?? ""} onChange={set("sMin")} />
            <GridNumber id="uMax" label="U value max" value={constraints.uMax ?? ""} onChange={set("uMax")} disabled={lockUmax} />
            <GridNumber id="vMax" label="Tvis max" value={constraints.vMax ?? ""} onChange={set("vMax")} />
            <GridNumber id="sMax" label="SHGC max" value={constraints.sMax ?? ""} onChange={set("sMax")} />
        </div>
    );
}

/* ------------------------------- API helpers ------------------------------- */

async function scanPdfAndCompute(file) {
    const form = new FormData();
    form.append("file", file);
    const { data } = await axiosInstance.post("/finder/scan-pdf/async", form, {
        headers: { "Content-Type": "multipart/form-data" },
    });
    return data;
}

async function waitForJobResult(jobPath, { pollIntervalMs = 1200, timeoutMs = 20 * 60 * 1000 } = {}) {
    const startedAt = Date.now();
    const transientStatuses = new Set([502, 503, 504]);
    let retryCount = 0;

    while (Date.now() - startedAt < timeoutMs) {
        try {
            const { data } = await axiosInstance.get(jobPath);
            const status = data?.status;

            if (status === "completed") {
                return data?.result;
            }

            if (status === "failed") {
                throw new Error(data?.error || "Background job failed");
            }

            retryCount = 0;
            await new Promise((resolve) => setTimeout(resolve, pollIntervalMs));
        } catch (error) {
            const status = error?.response?.status;
            if (!transientStatuses.has(status) || retryCount >= 5) {
                throw error;
            }

            retryCount += 1;
            const backoffMs = Math.min(pollIntervalMs * (retryCount + 1), 5000);
            await new Promise((resolve) => setTimeout(resolve, backoffMs));
        }
    }

    throw new Error("Background job timed out");
}

async function getDoorNames({ manufacturer, productType, material } = {}) {
    const params = sanitize({
        manufacturer,
        product_type: productType,   // maps to operator_type
        material,                    // maps to frame_material
    });

    const { data } = await axiosInstance.get("/finder/door/names", { params });
    const arr = Array.isArray(data) ? data : [];

    return arr
        .map((d) => {
            const name = (d?.name || "").trim();
            const frame_desc = (d?.frame_desc || "").trim();

            return {
                value: name,
                label: frame_desc ? `${name} - ${frame_desc}` : name,
            };
        })
        .filter((d) => d.value);
}

async function getGlassFinder({ windowName, constraints }) {
    const params = sanitize({
        window_name: windowName,
        u_min: constraints.uMin,
        u_max: constraints.uMax,
        s_min: constraints.sMin,
        s_max: constraints.sMax,
        v_min: constraints.vMin,
        v_max: constraints.vMax,
    });
    const { data } = await axiosInstance.get("/finder/glass", { params });
    return data;
}

/* -------------------------------- Component -------------------------------- */

export default function PDFGlassFinder({ showToastContainer = true }) {
    const [pdfFile, setPdfFile] = useState(null);
    const [processing, setProcessing] = useState(false);
    const [scanResult, setScanResult] = useState(null);

    const [materialFilter, setMaterialFilter] = useState("all");
    const [eligibilityFilter, setEligibilityFilter] = useState("eligible");
    const [selectedMfrs, setSelectedMfrs] = useState([]);
    const [activeType, setActiveType] = useState("");

    const [sortKey, setSortKey] = useState("u_value");
    const [sortDir, setSortDir] = useState("asc");

    const [manual, setManual] = useState({});
    const [expandedMfrs, setExpandedMfrs] = useState(() => new Set());
    const [manualLimit, setManualLimit] = useState(10);

    const [doorNameCache, setDoorNameCache] = useState({});

    function buildDoorNameCacheKey({ manufacturer = "", type = "", material = "", scope = "scoped" }) {
        return `${scope}__${manufacturer}__${type}__${material || "all-materials"}`;
    }

    function getScopedMaterialValue() {
        return materialFilter && materialFilter !== "all" ? materialFilter : undefined;
    }

    function getDoorOptionsForEntry(manufacturer, type, scope = "scoped") {
        const material = scope === "scoped" ? getScopedMaterialValue() : undefined;
        const key = buildDoorNameCacheKey({ manufacturer, type, material, scope });
        return doorNameCache[key] || [];
    }

    async function loadDoorOptionsForEntry(manufacturer, type, scope = "scoped") {
        const material = scope === "scoped" ? getScopedMaterialValue() : undefined;
        const key = buildDoorNameCacheKey({ manufacturer, type, material, scope });

        if (doorNameCache[key]) return;

        setManualField(manufacturer, type, { doorNamesLoading: true });

        try {
            const rows =
                scope === "all"
                    ? await getDoorNames()
                    : await getDoorNames({
                        manufacturer,
                        productType: type,
                        material,
                    });

            setDoorNameCache((prev) => ({
                ...prev,
                [key]: rows,
            }));
        } catch (e) {
            toast.error(`Failed to load systems: ${errMsg(e)}`);
        } finally {
            setManualField(manufacturer, type, { doorNamesLoading: false });
        }
    }

    async function handleManualScopeChange(manufacturer, type, scope) {
        setManualField(manufacturer, type, {
            scopeMode: scope,
            windowName: "",
            rows: [],
        });

        await loadDoorOptionsForEntry(manufacturer, type, scope);
    }

    const handleDrop = React.useCallback((acceptedFiles, fileRejections) => {
        const rejected = fileRejections?.[0];
        if (rejected) {
            toast.error("Please drop a single PDF file");
            return;
        }

        const file = acceptedFiles?.[0];
        if (!file) return;

        const isPdf =
            file.type === "application/pdf" ||
            String(file.name || "").toLowerCase().endsWith(".pdf");

        if (!isPdf) {
            toast.error("Please select a PDF file");
            return;
        }

        setPdfFile(file);
    }, []);

    const {
        getRootProps,
        getInputProps,
        isDragActive,
        isDragAccept,
        isDragReject,
        open,
    } = useDropzone({
        onDrop: handleDrop,
        accept: {
            "application/pdf": [".pdf"],
        },
        maxFiles: 1,
        multiple: false,
        noClick: true,
        noKeyboard: true,
    });

    const requiredTypes = useMemo(() => scanResult?.required_types || [], [scanResult]);

    const targetsByTypeMap = useMemo(() => {
        const out = {};
        for (const r of scanResult?.targets_by_type || []) out[r.product_type] = r.target_u_strict;
        return out;
    }, [scanResult]);

    const manufacturers = useMemo(() => scanResult?.manufacturers || [], [scanResult]);

    const availableMaterials = useMemo(() => {
        const fromBackend = Array.isArray(scanResult?.materials) ? scanResult.materials : null;
        if (fromBackend && fromBackend.length) return fromBackend;

        const set = new Set();
        for (const m of manufacturers) {
            for (const p of m.products || []) {
                const fm = (p.frame_material || "").trim();
                if (fm) set.add(fm);
            }
        }
        return Array.from(set).sort((a, b) => a.localeCompare(b));
    }, [scanResult, manufacturers]);

    const manufacturersByMaterial = useMemo(() => {
        return manufacturers
            .map((m) => {
                const allRows = Array.isArray(m.all_products)
                    ? m.all_products
                    : Array.isArray(m.products)
                        ? m.products
                        : [];

                const scopedAll =
                    !materialFilter || materialFilter === "all"
                        ? allRows
                        : allRows.filter(
                            (p) => String(p.frame_material || "").trim() === materialFilter
                        );

                if (scopedAll.length === 0) return null;

                const scopedQualified = scopedAll.filter((p) => !!p.meets_type_target);

                const presentTypes = new Set(
                    scopedAll
                        .map((p) => String(p.product_type || "").trim())
                        .filter(Boolean)
                );

                const qualifiedTypes = new Set(
                    scopedQualified
                        .map((p) => String(p.product_type || "").trim())
                        .filter(Boolean)
                );

                const missingRequiredTypes = requiredTypes.filter(
                    (t) => !presentTypes.has(t)
                );

                const failedTypes = requiredTypes.filter(
                    (t) => presentTypes.has(t) && !qualifiedTypes.has(t)
                );

                const eligible =
                    missingRequiredTypes.length === 0 && failedTypes.length === 0;

                const reason =
                    missingRequiredTypes.length > 0
                        ? "missing_types"
                        : failedTypes.length > 0
                            ? "fails_target"
                            : "meets_all";

                const per_type = requiredTypes
                    .filter((t) => presentTypes.has(t))
                    .map((t) => {
                        const rowsT = scopedAll.filter((p) => p.product_type === t);
                        const nums = rowsT
                            .map((p) => Number(p.u_value))
                            .filter((n) => Number.isFinite(n));

                        const minU = nums.length ? Math.min(...nums) : null;
                        const targetU = Number(targetsByTypeMap[t]);

                        return {
                            product_type: t,
                            target_u: Number.isFinite(targetU) ? targetU : null,
                            min_u_in_range: minU,
                            meets_type_target: qualifiedTypes.has(t),
                        };
                    });

                return {
                    ...m,
                    all_products: scopedAll,
                    products: scopedQualified,
                    eligible,
                    reason,
                    missing_required_types: missingRequiredTypes,
                    failed_types: failedTypes,
                    per_type,
                };
            })
            .filter(Boolean);
    }, [manufacturers, materialFilter, requiredTypes, targetsByTypeMap]);


    const manufacturersInScope = useMemo(() => {
        const base = manufacturersByMaterial;
        if (eligibilityFilter === "all") return base;
        if (eligibilityFilter === "eligible") return base.filter((m) => !!m.eligible);
        return base.filter((m) => !m.eligible);
    }, [manufacturersByMaterial, eligibilityFilter]);

    const availableMfrNames = useMemo(
        () => manufacturersInScope.map((m) => m.manufacturer).sort((a, b) => a.localeCompare(b)),
        [manufacturersInScope]
    );

    const effectiveSelected = useMemo(() => {
        if (!selectedMfrs || selectedMfrs.length === 0) return new Set(availableMfrNames);
        return new Set(selectedMfrs);
    }, [selectedMfrs, availableMfrNames]);

    const filteredMfrObjects = useMemo(
        () => manufacturersInScope.filter((m) => effectiveSelected.has(m.manufacturer)),
        [manufacturersInScope, effectiveSelected]
    );

    const ineligibleInView = useMemo(() => filteredMfrObjects.filter((m) => !m.eligible), [filteredMfrObjects]);

    const productColumns = useMemo(() => {
        const headerBtn = (label, key) => {
            const active = sortKey === key;
            const dir = active ? sortDir : "asc";
            return (
                <button
                    type="button"
                    onClick={() => {
                        setSortKey(key);
                        setSortDir((prev) => (active ? (prev === "asc" ? "desc" : "asc") : "asc"));
                    }}
                    className={cx(
                        "inline-flex items-center gap-2 rounded-lg px-2 py-1 -mx-2 hover:bg-slate-100",
                        active && "bg-slate-100"
                    )}
                    title="Sort"
                >
                    <span>{label}</span>
                    <ArrowUpDown className={cx("h-4 w-4", active ? "text-slate-700" : "text-slate-400")} />
                    <span className="sr-only">{active ? `sorted ${dir}` : "not sorted"}</span>
                </button>
            );
        };

        return [
            { key: "manufacturer", header: "Manufacturer" },
            { key: "product_type", header: "Product type" },
            { key: "frame_desc", header: "Frame description" },
            { key: "glass", header: "Glass description" },
            { key: "u_value", header: headerBtn("U value", "u_value") },
            { key: "target_u", header: "Target U" },
            { key: "window_code", header: headerBtn("Window code", "window_code") },
        ];
    }, [sortKey, sortDir]);

    const productsForActiveType = useMemo(() => {
        if (!activeType) return [];
        const rows = [];
        for (const m of filteredMfrObjects) {
            for (const p of m.products || []) {
                if (p.product_type === activeType) rows.push(p);
            }
        }

        const dirMul = sortDir === "asc" ? 1 : -1;

        rows.sort((a, b) => {
            if (sortKey === "window_code") {
                const aw = (a.window_code || "").localeCompare(b.window_code || "");
                if (aw !== 0) return aw * dirMul;
            } else {
                const au = Number(a.u_value);
                const bu = Number(b.u_value);
                const af = Number.isFinite(au) ? au : Number.POSITIVE_INFINITY;
                const bf = Number.isFinite(bu) ? bu : Number.POSITIVE_INFINITY;
                if (af !== bf) return (af - bf) * dirMul;
            }

            const am = (a.manufacturer || "").localeCompare(b.manufacturer || "");
            if (am !== 0) return am;
            const au2 = Number(a.u_value);
            const bu2 = Number(b.u_value);
            if (Number.isFinite(au2) && Number.isFinite(bu2) && au2 !== bu2) return au2 - bu2;
            return (a.window_code || "").localeCompare(b.window_code || "");
        });

        return rows;
    }, [filteredMfrObjects, activeType, sortKey, sortDir]);

    const glassColumns = useMemo(
        () => [
            { key: "glass", header: "Glass" },
            { key: "popularity", header: "Popularity" },
            { key: "supplier", header: "Supplier" },
            { key: "u", header: "U value" },
            { key: "s", header: "SHGC" },
            { key: "v", header: "Tvis" },
        ],
        []
    );

    const toggleMfr = (name) => {
        setSelectedMfrs((prev) => {
            const set = new Set(prev);
            if (set.has(name)) set.delete(name);
            else set.add(name);
            return Array.from(set);
        });
    };

    const toggleExpand = async (manufacturer, needs) => {
        const willOpen = !expandedMfrs.has(manufacturer);

        setExpandedMfrs((prev) => {
            const next = new Set(prev);
            if (next.has(manufacturer)) next.delete(manufacturer);
            else next.add(manufacturer);
            return next;
        });

        if (!willOpen) return;

        await Promise.all(
            needs.map((type) => {
                const key = `${manufacturer}__${type}`;
                const scopeMode = manual[key]?.scopeMode || "scoped";

                setManualField(manufacturer, type, {
                    scopeMode,
                    doorNamesLoading: false,
                });

                return loadDoorOptionsForEntry(manufacturer, type, scopeMode);
            })
        );
    };

    const resetAll = () => {
        setPdfFile(null);
        setProcessing(false);
        setScanResult(null);

        setMaterialFilter("all");
        setEligibilityFilter("eligible");

        setSelectedMfrs([]);
        setActiveType("");
        setSortKey("u_value");
        setSortDir("asc");

        setManual({});
        setExpandedMfrs(new Set());
        setManualLimit(10);

        setDoorNameCache({});
    };

    const runScan = async () => {
        try {
            if (!pdfFile) {
                toast.error("Select a PDF first");
                return;
            }
            setProcessing(true);

            const { job_id: jobId } = await scanPdfAndCompute(pdfFile);
            toast.info("PDF uploaded. Processing in background...");
            const data = await waitForJobResult(`/finder/jobs/${jobId}`);
            setScanResult(data);

            const firstType = (data?.required_types || [])[0] || "";
            setActiveType(firstType);

            setMaterialFilter("all");
            setEligibilityFilter("eligible");
            setSelectedMfrs([]);

            setSortKey("u_value");
            setSortDir("asc");

            setManual({});
            setExpandedMfrs(new Set());
            setManualLimit(10);
            setDoorNameCache({});

            const eligibleCount = (data?.manufacturers || []).filter((m) => m.eligible).length;
            toast.success(`Processed PDF. Eligible manufacturers: ${eligibleCount}/${(data?.manufacturers || []).length}`);
        } catch (e) {
            setScanResult(null);
            toast.error(`PDF scan failed: ${friendlyNetworkError(e)}`);
        } finally {
            setProcessing(false);
        }
    };

    const setManualField = (mfr, type, patch) => {
        const key = `${mfr}__${type}`;
        setManual((prev) => ({
            ...prev,
            [key]: {
                windowName: "",
                constraints: {},
                loading: false,
                rows: [],
                scopeMode: "scoped",
                doorNamesLoading: false,
                ...(prev[key] || {}),
                ...patch,
            },
        }));
    };

    const submitManual = async (mfr, type) => {
        const key = `${mfr}__${type}`;
        const entry = manual[key] || {};
        if (!entry.windowName) {
            toast.error("Select a system first");
            return;
        }

        const targetU = targetsByTypeMap[type];
        if (typeof targetU !== "number" || !Number.isFinite(targetU)) {
            toast.error("No target U available for this type");
            return;
        }

        const constraints = { ...(entry.constraints || {}) };
        constraints.uMax = targetU;
        if (constraints.uMin == null || constraints.uMin === "") constraints.uMin = 0;

        setManualField(mfr, type, { loading: true });

        try {
            const data = await getGlassFinder({ windowName: entry.windowName, constraints });
            const rows = Array.isArray(data) ? data : [];
            setManualField(mfr, type, { rows: rows.map((r) => ({ ...r, popularity: r.popularity ?? 0 })) });
            if (rows.length === 0) toast.info("No results found");
        } catch (e) {
            setManualField(mfr, type, { rows: [] });
            toast.error(`Glass Finder failed: ${errMsg(e)}`);
        } finally {
            setManualField(mfr, type, { loading: false });
        }
    };

    const perSelectedMfrMaxMin = useMemo(() => {
        if (!scanResult) return [];
        if (!selectedMfrs || selectedMfrs.length === 0) return [];

        const out = [];
        const selected = new Set(selectedMfrs);

        for (const m of manufacturersInScope) {
            if (!selected.has(m.manufacturer)) continue;

            const prods = Array.isArray(m.products) ? m.products : [];

            const byType = {};
            for (const t of requiredTypes) {
                const rowsT = prods.filter((p) => p.product_type === t);
                if (!rowsT.length) {
                    byType[t] = { min: null, max: null };
                    continue;
                }
                const sorted = [...rowsT].sort((a, b) => {
                    const au = Number(a.u_value);
                    const bu = Number(b.u_value);
                    if (Number.isFinite(au) && Number.isFinite(bu) && au !== bu) return au - bu;
                    const aw = (a.window_code || "").localeCompare(b.window_code || "");
                    if (aw !== 0) return aw;
                    return (a.glass || "").localeCompare(b.glass || "");
                });
                byType[t] = { min: sorted[0], max: sorted[sorted.length - 1] };
            }

            out.push({ manufacturer: m.manufacturer, byType });
        }

        out.sort((a, b) => a.manufacturer.localeCompare(b.manufacturer));
        return out;
    }, [scanResult, selectedMfrs, manufacturersInScope, requiredTypes]);

    return (
        <div className="flex flex-col gap-4">
            {/* Upload */}
            <section className="rounded-2xl border border-slate-200 bg-white shadow-sm">
                <div className="px-5 py-5 sm:px-6 sm:py-6">
                    <div className="flex items-start gap-4 text-left">
                        <div className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-slate-900 text-white">
                            <UploadCloud className="h-5 w-5" />
                        </div>

                        <div className="min-w-0 w-full">
                            <h2 className="text-lg sm:text-xl font-bold text-slate-900">PDF Glass Finder</h2>
                            <p className="mt-1 text-xs sm:text-sm text-slate-500">
                                Upload a PDF to extract window codes and generate target U by product type, then list eligible/ineligible manufacturers and products.
                            </p>

                            <input {...getInputProps()} />

                            <div
                                {...getRootProps()}
                                className={cx(
                                    "mt-5 rounded-2xl border-2 border-dashed p-6 sm:p-8 transition-colors",
                                    "bg-slate-50",
                                    isDragAccept && "border-emerald-400 bg-emerald-50",
                                    isDragReject && "border-rose-400 bg-rose-50",
                                    isDragActive && !isDragReject && "border-slate-400 bg-slate-100",
                                    !isDragActive && !isDragAccept && !isDragReject && "border-slate-300 hover:border-slate-400"
                                )}
                            >
                                <div className="flex gap-12 lg:gap-32 flex-col lg:flex-row items-center justify-center">
                                    <div className="flex flex-col items-center justify-center text-center lg:items-start lg:text-left">
                                        <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-white shadow-sm border border-slate-200">
                                            <UploadCloud className="h-7 w-7 text-slate-700" />
                                        </div>

                                        <div className="mt-4">
                                            <h3 className="text-sm sm:text-base font-bold text-slate-900">
                                                {isDragActive ? "Drop your PDF here" : "Drag and drop your PDF here"}
                                            </h3>
                                            <p className="mt-1 text-xs sm:text-sm text-slate-500">
                                                PDF only, one file at a time
                                            </p>
                                        </div>

                                        <button
                                            type="button"
                                            onClick={open}
                                            className="mt-4 h-11 rounded-xl border border-slate-200 bg-white px-5 text-sm font-semibold text-slate-700 shadow-sm hover:bg-slate-100 disabled:opacity-60"
                                            disabled={processing}
                                        >
                                            <span className="inline-flex items-center gap-2">
                                                <FileText className="h-4 w-4 text-slate-400" />
                                                {pdfFile ? "Replace PDF" : "Browse file"}
                                            </span>
                                        </button>
                                    </div>

                                    <div className="flex flex-col items-center text-center">
                                        <img
                                            src="./nathers_report_example.png"
                                            alt="Example NatHERS report to upload"
                                            className="w-full max-w-[320px] rounded-2xl border border-slate-200 bg-white object-contain shadow-sm"
                                        />
                                        <p className="mt-2 text-xs text-slate-500">
                                            Example of the report format to upload
                                        </p>
                                    </div>
                                </div>
                            </div>

                            <div className="mt-5 flex justify-center">
                                <button
                                    type="button"
                                    onClick={runScan}
                                    className="h-11 rounded-xl lg:w-1/2 bg-slate-900 px-6 text-sm font-semibold text-white shadow-sm hover:bg-slate-800 disabled:opacity-60 sm:col-start-2"
                                    disabled={processing || !pdfFile}
                                >
                                    <span className="inline-flex items-center gap-2">
                                        <Search className="h-4 w-4" />
                                        {processing ? "Processing..." : "Scan & Compute"}
                                    </span>
                                </button>
                            </div>

                            {pdfFile && (
                                <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
                                    <div className="text-xs sm:text-sm text-slate-600">
                                        Selected: <span className="font-semibold text-slate-900">{pdfFile.name}</span>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={resetAll}
                                        className="h-9 rounded-full border border-slate-200 bg-white px-4 text-xs sm:text-sm font-semibold text-slate-700 shadow-sm hover:bg-slate-100 disabled:opacity-60"
                                        disabled={processing}
                                    >
                                        <span className="inline-flex items-center gap-2">
                                            <RotateCcw className="h-4 w-4 text-slate-400" />
                                            Reset
                                        </span>
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </section>

            {/* Filters */}
            {scanResult && (
                <section className="rounded-2xl border border-slate-200 bg-white shadow-sm">
                    <div className="px-5 py-5 sm:px-6 sm:py-6">
                        <div className="flex items-start gap-4 text-left">
                            <div className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-slate-900 text-white">
                                <Filter className="h-5 w-5" />
                            </div>

                            <div className="min-w-0 w-full">
                                <div className="flex flex-wrap items-center justify-between gap-3">
                                    <div>
                                        <h3 className="text-lg sm:text-xl font-bold text-slate-900">Filters</h3>
                                        <p className="mt-1 text-xs sm:text-sm text-slate-500">
                                            Material filter applies first. Then Eligible = meets all types; Ineligible = missing types or fails target; All = both.
                                        </p>
                                    </div>

                                    <div className="flex flex-wrap items-center gap-2">
                                        <Badge tone="slate">Codes: {scanResult?.codes?.length ?? 0}</Badge>
                                        <Badge tone="emerald">Eligible: {(manufacturersByMaterial || []).filter((m) => m.eligible).length}</Badge>
                                        <Badge tone="rose">Ineligible: {(manufacturersByMaterial || []).filter((m) => !m.eligible).length}</Badge>
                                    </div>
                                </div>

                                <div className="mt-5 grid grid-cols-1 gap-3">
                                    <div>
                                        <label className="text-sm font-semibold text-slate-700">Material</label>
                                        <select
                                            value={materialFilter}
                                            onChange={(e) => {
                                                setMaterialFilter(e.target.value);
                                                setSelectedMfrs([]);
                                                setManual({});
                                                setExpandedMfrs(new Set());
                                                setDoorNameCache({});
                                                setManualLimit(10);
                                            }}
                                            className="mt-1 h-11 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-900 shadow-sm outline-none focus:ring-2 focus:ring-slate-400/70"
                                        >
                                            <option value="all">All materials</option>
                                            {availableMaterials.map((m) => (
                                                <option key={m} value={m}>
                                                    {m}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    <div className="lg:col-span-2 flex flex-col gap-3">
                                        <div className="flex flex-wrap items-center justify-between gap-3">
                                            <PillTabs
                                                value={eligibilityFilter}
                                                onChange={setEligibilityFilter}
                                                tabs={[
                                                    { value: "eligible", label: "Eligible" },
                                                    { value: "ineligible", label: "Ineligible" },
                                                    { value: "all", label: "All" },
                                                ]}
                                            />
                                            <button
                                                type="button"
                                                onClick={() => setSelectedMfrs([])}
                                                className="h-9 rounded-full border border-slate-200 bg-white px-4 text-xs sm:text-sm font-semibold text-slate-700 shadow-sm hover:bg-slate-100"
                                            >
                                                Clear selection
                                            </button>
                                        </div>

                                        <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                                            <div className="text-sm font-bold text-slate-900">Manufacturers</div>
                                            <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
                                                {availableMfrNames.map((m) => {
                                                    const selected = selectedMfrs.length === 0 ? true : effectiveSelected.has(m);
                                                    const showSelectedStyle = selectedMfrs.length > 0 && selected;

                                                    return (
                                                        <button
                                                            key={m}
                                                            type="button"
                                                            onClick={() => toggleMfr(m)}
                                                            className={cx(
                                                                "flex items-center justify-between gap-3 rounded-xl border px-3 py-2 text-left text-sm shadow-sm",
                                                                selectedMfrs.length === 0
                                                                    ? "border-slate-200 bg-white hover:bg-slate-100"
                                                                    : showSelectedStyle
                                                                        ? "border-slate-900 bg-slate-900 text-white"
                                                                        : "border-slate-200 bg-white text-slate-700 hover:bg-slate-100"
                                                            )}
                                                        >
                                                            <span className="truncate">{m}</span>
                                                            {selectedMfrs.length === 0 ? (
                                                                <span className="text-xs font-semibold text-slate-500">All</span>
                                                            ) : showSelectedStyle ? (
                                                                <CheckCircle2 className="h-4 w-4 shrink-0" />
                                                            ) : (
                                                                <XCircle className="h-4 w-4 shrink-0 text-slate-300" />
                                                            )}
                                                        </button>
                                                    );
                                                })}
                                            </div>

                                            <div className="mt-3 text-xs sm:text-sm text-slate-600">
                                                Tip: if no manufacturers are selected, all manufacturers in the current material + eligibility view are included.
                                            </div>
                                        </div>

                                        <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 text-xs sm:text-sm font-semibold text-slate-700 shadow-sm">
                                            <Info className="h-4 w-4 text-slate-400" />
                                            Product types are derived from the PDF codes.
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
            )}

            {/* Targets + Max/Min (per selected manufacturer) */}
            {scanResult && (
                <section className="rounded-2xl border border-slate-200 bg-white shadow-sm">
                    <div className="px-5 py-5 sm:px-6 sm:py-6">
                        <div className="flex items-start gap-4 text-left">
                            <div className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-slate-900 text-white">
                                <Info className="h-5 w-5" />
                            </div>

                            <div className="min-w-0 w-full">
                                <h3 className="text-lg sm:text-xl font-bold text-slate-900">Targets and max/min sets</h3>
                                <p className="mt-1 text-xs sm:text-sm text-slate-500">
                                    Targets are strictest from extracted codes per product type. Max/min sets are shown per selected manufacturer (meeting target), respecting the material filter.
                                </p>

                                <div className="mt-5 grid grid-cols-1 gap-4">
                                    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                                        <div className="text-sm font-bold text-slate-900">Targets by type</div>
                                        <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                                            {requiredTypes.map((t) => (
                                                <div key={t} className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
                                                    <div className="text-xs font-semibold text-slate-500">{t}</div>
                                                    <div className="mt-1 text-lg font-extrabold text-slate-900">
                                                        {typeof targetsByTypeMap[t] === "number" ? Number(targetsByTypeMap[t]).toFixed(3) : "—"}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                                        <div className="flex flex-wrap items-center justify-between gap-2">
                                            <div className="text-sm font-bold text-slate-900">Max/min sets (selected manufacturers)</div>
                                            <Badge tone="slate">
                                                {selectedMfrs.length === 0 ? "Select manufacturers to view" : `Selected: ${selectedMfrs.length}`}
                                            </Badge>
                                        </div>

                                        {selectedMfrs.length === 0 ? (
                                            <div className="mt-3 rounded-xl border border-slate-200 bg-white p-3 text-sm text-slate-600">
                                                Select one or more manufacturers above to view max/min per manufacturer.
                                            </div>
                                        ) : (
                                            <div className="mt-3 flex flex-col gap-3">
                                                {perSelectedMfrMaxMin.map((mm) => (
                                                    <div key={mm.manufacturer} className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
                                                        <div className="flex flex-wrap items-center justify-between gap-2">
                                                            <div className="text-sm font-extrabold text-slate-900">{mm.manufacturer}</div>
                                                            {materialFilter !== "all" && <Badge tone="amber">{materialFilter}</Badge>}
                                                        </div>

                                                        <div className="mt-3 grid grid-cols-1 gap-3">
                                                            {requiredTypes.map((t) => {
                                                                const rec = mm.byType?.[t] || { min: null, max: null };
                                                                const min = rec.min;
                                                                const max = rec.max;

                                                                return (
                                                                    <div key={t} className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                                                                        <div className="flex flex-wrap items-center justify-between gap-2">
                                                                            <div className="text-xs font-semibold text-slate-600">{t}</div>
                                                                            <Badge tone="slate">Target {Number(targetsByTypeMap[t]).toFixed(3)}</Badge>
                                                                        </div>

                                                                        <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2">
                                                                            <div className="rounded-xl border border-slate-200 bg-white p-3">
                                                                                <div className="text-xs font-semibold text-slate-600">Min U (lowest)</div>
                                                                                <div className="mt-1 text-sm text-slate-900">
                                                                                    {min ? (
                                                                                        <>
                                                                                            <div className="font-semibold">{min.window_code}</div>
                                                                                            <div className="text-xs text-slate-600">
                                                                                                U {Number(min.u_value).toFixed(3)}
                                                                                                {min.frame_desc ? ` · ${min.frame_desc}` : ""}
                                                                                            </div>
                                                                                        </>
                                                                                    ) : (
                                                                                        <span className="text-slate-500">—</span>
                                                                                    )}
                                                                                </div>
                                                                            </div>

                                                                            <div className="rounded-xl border border-slate-200 bg-white p-3">
                                                                                <div className="text-xs font-semibold text-slate-600">Max U (highest)</div>
                                                                                <div className="mt-1 text-sm text-slate-900">
                                                                                    {max ? (
                                                                                        <>
                                                                                            <div className="font-semibold">{max.window_code}</div>
                                                                                            <div className="text-xs text-slate-600">
                                                                                                U {Number(max.u_value).toFixed(3)}
                                                                                                {max.frame_desc ? ` · ${max.frame_desc}` : ""}
                                                                                            </div>
                                                                                        </>
                                                                                    ) : (
                                                                                        <span className="text-slate-500">—</span>
                                                                                    )}
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    </div>
                                                ))}

                                                {perSelectedMfrMaxMin.length === 0 && (
                                                    <div className="rounded-xl border border-slate-200 bg-white p-3 text-sm text-slate-600">
                                                        No max/min data found for the selected manufacturers in the current view.
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
            )}

            {/* Products */}
            {scanResult && (
                <section className="rounded-2xl border border-slate-200 bg-white shadow-sm">
                    <div className="px-5 py-5 sm:px-6 sm:py-6">
                        <div className="flex items-start gap-4 text-left">
                            <div className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-slate-900 text-white">
                                <Table2 className="h-5 w-5" />
                            </div>

                            <div className="min-w-0 w-full">
                                <div className="flex flex-wrap items-start justify-between gap-3">
                                    <div>
                                        <h3 className="text-lg sm:text-xl font-bold text-slate-900">Products</h3>
                                        <p className="mt-1 text-xs sm:text-sm text-slate-500">
                                            Products shown are those meeting the target U per product type. Click U value or Window code headers to sort.
                                        </p>
                                    </div>

                                    <PillTabs value={activeType} onChange={setActiveType} tabs={requiredTypes.map((t) => ({ value: t, label: t }))} />
                                </div>

                                <div className="mt-4">
                                    <ScrollTable columns={productColumns} rows={productsForActiveType} />
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
            )}

            {/* Manual completion (lazy + limited) */}
            {scanResult && eligibilityFilter !== "eligible" && (
                <section className="rounded-2xl border border-slate-200 bg-white shadow-sm">
                    <div className="px-5 py-5 sm:px-6 sm:py-6">
                        <div className="flex items-start gap-4 text-left">
                            <div className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-slate-900 text-white">
                                <AlertTriangle className="h-5 w-5" />
                            </div>

                            <div className="min-w-0 w-full">
                                <h3 className="text-lg sm:text-xl font-bold text-slate-900">Manual completion (ineligible manufacturers)</h3>
                                <p className="mt-1 text-xs sm:text-sm text-slate-500">Expand a manufacturer to load forms for missing/failed types.</p>

                                <div className="mt-4 flex flex-wrap items-center justify-between gap-2">
                                    <div className="text-xs sm:text-sm text-slate-600">
                                        Showing {Math.min(manualLimit, ineligibleInView.length)} of {ineligibleInView.length} ineligible manufacturers
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <button
                                            type="button"
                                            onClick={() => setManualLimit((n) => Math.min(ineligibleInView.length, n + 10))}
                                            className="h-9 rounded-full border border-slate-200 bg-white px-4 text-xs sm:text-sm font-semibold text-slate-700 shadow-sm hover:bg-slate-100 disabled:opacity-50"
                                            disabled={manualLimit >= ineligibleInView.length}
                                        >
                                            Show 10 more
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => setManualLimit(10)}
                                            className="h-9 rounded-full border border-slate-200 bg-white px-4 text-xs sm:text-sm font-semibold text-slate-700 shadow-sm hover:bg-slate-100 disabled:opacity-50"
                                            disabled={manualLimit === 10}
                                        >
                                            Reset
                                        </button>
                                    </div>
                                </div>

                                <div className="mt-4 flex flex-col gap-4">
                                    {ineligibleInView.slice(0, manualLimit).map((m) => {
                                        const missing = Array.isArray(m.missing_required_types) ? m.missing_required_types : [];
                                        const failed = Array.isArray(m.failed_types) ? m.failed_types : [];
                                        const needs = Array.from(new Set([...missing, ...failed])).filter(Boolean);
                                        if (needs.length === 0) return null;

                                        const isOpen = expandedMfrs.has(m.manufacturer);

                                        return (
                                            <div key={m.manufacturer} className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                                                <div className="flex flex-wrap items-center justify-between gap-2">
                                                    <div className="min-w-0">
                                                        <div className="text-sm font-extrabold text-slate-900 truncate">{m.manufacturer}</div>
                                                        <div className="mt-1 flex flex-wrap items-center gap-2">
                                                            <Badge tone="rose">Ineligible</Badge>
                                                            {m.reason && <Badge tone="amber">{m.reason}</Badge>}
                                                            {missing.length > 0 && <Badge tone="amber">Missing: {missing.join(", ")}</Badge>}
                                                            {failed.length > 0 && <Badge tone="amber">Failed: {failed.join(", ")}</Badge>}
                                                        </div>
                                                    </div>

                                                    <button
                                                        type="button"
                                                        onClick={() => toggleExpand(m.manufacturer, needs)}
                                                        className="h-9 rounded-full border border-slate-200 bg-white px-4 text-xs sm:text-sm font-semibold text-slate-700 shadow-sm hover:bg-slate-100"
                                                    >
                                                        {isOpen ? "Collapse" : `Expand (${needs.length})`}
                                                    </button>
                                                </div>

                                                {isOpen && (
                                                    <div className="mt-4 grid grid-cols-1 gap-4">
                                                        {needs.map((type) => {
                                                            const key = `${m.manufacturer}__${type}`;
                                                            const entry = manual[key] || { windowName: "", constraints: {}, loading: false, rows: [] };
                                                            const targetU = targetsByTypeMap[type];
                                                            const lockUmax = typeof targetU === "number" && Number.isFinite(targetU);

                                                            return (
                                                                <div key={type} className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                                                                    <div className="flex flex-wrap items-center justify-between gap-2">
                                                                        <div className="text-sm font-bold text-slate-900">{type} — Glass Finder</div>
                                                                        <Badge tone="slate">Target U {lockUmax ? targetU.toFixed(3) : "—"}</Badge>
                                                                    </div>

                                                                    <div className="mt-3 flex flex-col gap-3">
                                                                        {(() => {
                                                                            const scopeMode = entry.scopeMode || "scoped";
                                                                            const scopedMaterial = getScopedMaterialValue();
                                                                            const systemOptions = getDoorOptionsForEntry(m.manufacturer, type, scopeMode);

                                                                            return (
                                                                                <div className="flex flex-col gap-3">
                                                                                    <div className="flex flex-wrap items-center justify-between gap-2">
                                                                                        <label className="text-sm font-semibold text-slate-700">System scope</label>

                                                                                        <div className="inline-flex rounded-2xl border border-slate-200 bg-white shadow-sm">
                                                                                            <button
                                                                                                type="button"
                                                                                                onClick={() => handleManualScopeChange(m.manufacturer, type, "scoped")}
                                                                                                className={cx(
                                                                                                    "h-7 rounded-xl px-3 text-xs font-semibold",
                                                                                                    scopeMode === "scoped"
                                                                                                        ? "bg-slate-900 text-white"
                                                                                                        : "text-slate-700 hover:bg-slate-100"
                                                                                                )}
                                                                                            >
                                                                                                Scoped
                                                                                            </button>
                                                                                            <button
                                                                                                type="button"
                                                                                                onClick={() => handleManualScopeChange(m.manufacturer, type, "all")}
                                                                                                className={cx(
                                                                                                    "h-7 rounded-xl px-3 text-xs font-semibold",
                                                                                                    scopeMode === "all"
                                                                                                        ? "bg-slate-900 text-white"
                                                                                                        : "text-slate-700 hover:bg-slate-100"
                                                                                                )}
                                                                                            >
                                                                                                All systems
                                                                                            </button>
                                                                                        </div>
                                                                                    </div>

                                                                                    <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600">
                                                                                        {scopeMode === "scoped" ? (
                                                                                            <>
                                                                                                Showing systems for
                                                                                                <span className="font-semibold text-slate-900"> {m.manufacturer}</span>,
                                                                                                <span className="font-semibold text-slate-900"> {type}</span>
                                                                                                {scopedMaterial ? (
                                                                                                    <>
                                                                                                        , material
                                                                                                        <span className="font-semibold text-slate-900"> {scopedMaterial}</span>
                                                                                                    </>
                                                                                                ) : (
                                                                                                    <>
                                                                                                        , <span className="font-semibold text-slate-900">all materials</span>
                                                                                                    </>
                                                                                                )}
                                                                                            </>
                                                                                        ) : (
                                                                                            <>Showing all systems regardless of manufacturer, type, or material.</>
                                                                                        )}
                                                                                    </div>

                                                                                    <div className="flex flex-col gap-1">
                                                                                        <label className="text-sm font-semibold text-slate-700">System Name</label>
                                                                                        <select
                                                                                            value={entry.windowName || ""}
                                                                                            onChange={(e) => setManualField(m.manufacturer, type, { windowName: e.target.value })}
                                                                                            disabled={entry.doorNamesLoading}
                                                                                            className="h-11 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-900 shadow-sm outline-none focus:ring-2 focus:ring-slate-400/70 disabled:bg-slate-50 disabled:opacity-60"
                                                                                        >
                                                                                            <option value="" disabled>
                                                                                                {entry.doorNamesLoading ? "Loading systems..." : "Select system"}
                                                                                            </option>

                                                                                            {systemOptions.map(({ value, label }) => (
                                                                                                <option key={`${value}__${label}`} value={value}>
                                                                                                    {label}
                                                                                                </option>
                                                                                            ))}
                                                                                        </select>
                                                                                    </div>
                                                                                </div>
                                                                            );
                                                                        })()}

                                                                        <USVInputs
                                                                            constraints={{
                                                                                ...entry.constraints,
                                                                                ...(lockUmax ? { uMax: targetU } : {}),
                                                                            }}
                                                                            setConstraints={(c) => setManualField(m.manufacturer, type, { constraints: c })}
                                                                            lockUmax={lockUmax}
                                                                        />

                                                                        <div className="flex flex-wrap items-center justify-center gap-2">
                                                                            <button
                                                                                type="button"
                                                                                onClick={() => submitManual(m.manufacturer, type)}
                                                                                className="h-11 rounded-xl bg-slate-900 px-5 text-sm font-semibold text-white shadow-sm hover:bg-slate-800 disabled:opacity-60"
                                                                                disabled={entry.loading || !entry.windowName}
                                                                            >
                                                                                <span className="inline-flex items-center gap-2">
                                                                                    <Search className="h-4 w-4" />
                                                                                    {entry.loading ? "Finding..." : "Find glass"}
                                                                                </span>
                                                                            </button>

                                                                            <button
                                                                                type="button"
                                                                                onClick={() =>
                                                                                    setManualField(m.manufacturer, type, {
                                                                                        windowName: "",
                                                                                        constraints: {},
                                                                                        rows: [],
                                                                                        loading: false,
                                                                                    })
                                                                                }
                                                                                className="h-11 rounded-xl border border-slate-200 bg-white px-5 text-sm font-semibold text-slate-700 shadow-sm hover:bg-slate-100 disabled:opacity-60"
                                                                                disabled={entry.loading}
                                                                            >
                                                                                <span className="inline-flex items-center gap-2">
                                                                                    <RotateCcw className="h-4 w-4 text-slate-400" />
                                                                                    Reset
                                                                                </span>
                                                                            </button>
                                                                        </div>

                                                                        <ScrollTable columns={glassColumns} rows={entry.rows || []} maxHeight="max-h-[360px]" />
                                                                    </div>
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}

                                    {ineligibleInView.length === 0 && <div className="text-sm text-slate-600">No ineligible manufacturers in the current view.</div>}
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
            )}
            {showToastContainer && (
                <ToastContainer position="top-right" autoClose={4000} newestOnTop />
            )}
        </div>
    );
}
